import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Coffee, Dumbbell, Users, Sparkles } from "lucide-react";
import { signInAsGuest } from "@/lib/auth";
import { IMG } from "@/lib/mockData";
import Logo from "@/components/Logo";

const Landing = () => {
  const nav = useNavigate();

  const handleGuest = () => {
    signInAsGuest();
    nav("/feed");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="landing-page">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
        <Logo size={42} />
        <div className="flex items-center gap-2">
          <Link to="/login" className="brutal-btn bg-white px-4 py-2 text-sm" data-testid="landing-login-btn">
            Log in
          </Link>
          <Link to="/register" className="brutal-btn bg-[#0A0A0A] text-white px-4 py-2 text-sm" data-testid="landing-signup-btn">
            Sign up
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-8 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 brutal-border bg-[#FFFBEB] rounded-full px-3 py-1 mb-5">
            <span className="w-2 h-2 bg-[#FF6A2C] rounded-full live-ping" />
            <span className="label-mono">Not a dating app</span>
          </div>
          <h1 className="font-heading font-black text-5xl md:text-7xl tracking-tighter leading-[0.9] uppercase mb-5">
            Grabb someone.<br />
            <span className="text-[#FF6A2C]">Actually</span> go.
          </h1>
          <p className="text-lg text-[#555555] mb-7 max-w-md">
            Coffee, gym, a feed, a surf, a fish, a hit of pool. Find people on the Goldie doing the same thing nearby — and actually become mates.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="brutal-btn bg-[#FF6A2C] text-white px-6 py-3.5 inline-flex items-center justify-center gap-2" data-testid="landing-cta-signup">
              Get started — 100 free tokens
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button onClick={handleGuest} className="brutal-btn bg-white px-6 py-3.5" data-testid="landing-cta-guest">
              Browse as guest
            </button>
          </div>
        </div>

        <div className="relative">
          <img
            src={IMG.hero}
            alt="People grabbing coffee together"
            className="w-full h-[420px] object-cover brutal-border rounded-2xl brutal-shadow-lg"
          />
          <div className="absolute -bottom-5 -left-5 brutal-card p-3 max-w-[200px] bg-[#FFCC00]">
            <div className="label-mono mb-1">Now live</div>
            <div className="font-heading font-black text-2xl leading-none">142 people</div>
            <div className="text-xs text-[#555555] mt-1">on the Goldie up for something</div>
          </div>
          <div className="absolute -top-5 -right-5 brutal-card p-3 bg-[#C8E6C9]">
            <div className="font-mono font-bold text-xs">92% match</div>
            <div className="text-xs mt-0.5">Coffee — 1.2km</div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-8">How it works</h2>
        <div className="grid md:grid-cols-3 gap-5">
          <Step n="01" icon={Sparkles} title="Post or pick" body="Spin up an activity — gym, coffee, ramen — or join one nearby." />
          <Step n="02" icon={Users} title="Match by vibe" body="Real compatibility score from intent, vibe, distance and timing." />
          <Step n="03" icon={Coffee} title="Actually show up" body="Group chat, commit checks, post-activity rating. No flakes." />
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-6">What people grabb</h2>
        <div className="flex gap-3 flex-wrap">
          {["Coffee ☕", "Gym 💪", "Run 🏃", "Surf 🏄", "Fish 🎣", "Pool 🎱", "Food 🍽️", "Drinks 🍸", "Beach 🏖️", "Gaming 🎮"].map((t) => (
            <span key={t} className="brutal-border bg-white rounded-full px-4 py-2 font-bold text-sm brutal-shadow-sm">
              {t}
            </span>
          ))}
        </div>
      </section>

      {/* Communities */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-2">Find your people</h2>
        <p className="text-[#555555] mb-6 max-w-xl">Not just a meetup app — a friend-connecting app. Jump into a community that fits you.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Active", e: "⚡", c: "#FFCC00" },
            { l: "Fitness", e: "💪", c: "#C8E6C9" },
            { l: "Mums & Bubs", e: "👶", c: "#FFD8A8" },
            { l: "Foodies", e: "🍴", c: "#FF6A2C" },
            { l: "Runners", e: "🏃", c: "#A0CED9" },
            { l: "Surfers", e: "🏄", c: "#7FDBFF" },
            { l: "Sports", e: "⚽", c: "#B39DDB" },
            { l: "Gamers", e: "🎮", c: "#FFB3C1" },
            { l: "Fishos", e: "🎣", c: "#9DD9D2" },
            { l: "Students", e: "📚", c: "#FFFBEB" },
            { l: "New in town", e: "🆕", c: "#FF6F61" },
          ].map((com) => (
            <div key={com.l} className="brutal-card p-4" style={{ background: com.c }}>
              <div className="text-2xl mb-1">{com.e}</div>
              <div className="font-heading font-bold">{com.l}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t-2 border-[#0A0A0A] bg-white">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <span className="text-[#555555]">© Grabb. Built for showing up.</span>
          <div className="flex gap-4">
            <Link to="/admin" className="text-[#555555] hover:text-[#0A0A0A]" data-testid="footer-admin-link">Admin</Link>
            <Link to="/business/signup" className="text-[#555555] hover:text-[#0A0A0A]" data-testid="footer-business-link">List your venue</Link>
            <Link to="/business" className="text-[#555555] hover:text-[#0A0A0A]" data-testid="footer-business-portal-link">Venue portal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Step = ({ n, icon: Icon, title, body }) => (
  <div className="brutal-card p-6">
    <div className="flex items-center justify-between mb-4">
      <span className="font-mono font-bold text-sm text-[#555555]">{n}</span>
      <div className="w-10 h-10 bg-[#FFFBEB] brutal-border rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
    </div>
    <h3 className="font-heading font-bold text-xl mb-2">{title}</h3>
    <p className="text-sm text-[#555555]">{body}</p>
  </div>
);

export default Landing;
